import React, { useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { object, string, number, array, date } from "yup";
import { Row, Space } from "@masterteam/mt-design-react";

import { WizardStep, Field } from "../Wizard";

export const detailsValidationSchema = object().shape({
  ArabicName: string().required(" "),
  EnglishName: string().required(" "),
  ArabicDescription: string().required(" "),
  EnglishDescription: string().required(" "),
  StrategyType: object().required(" "),
  Strategy: object().nullable().required(" "),
  Perspective: object().nullable().required(" "),
  Theme: object()
    .nullable()
    .when("StrategyType", {
      is: (StrategyType) => +StrategyType?.Value === 1,
      then: (schema) => schema.required(" ")
    }),
  Pillar: object()
    .nullable()
    .when("StrategyType", {
      is: (StrategyType) => +StrategyType?.Value === 2,
      then: (schema) => schema.required(" ")
    }),
  Level: object()
    .nullable()
    .when("StrategyType", {
      is: (StrategyType) => +StrategyType?.Value === 1,
      then: (schema) => schema.required(" ")
    }),
  ParentID: object()
    .nullable()
    .when("Level", {
      is: (Level) => +Level?.Value === 2,
      then: (schema) => schema.required(" ")
    }),
  ObjectiveSectors: array().of(object()).min(1, " "),
  ObjectiveExecutiveDepartments: array()
    .of(object())
    .when(["Level", "StrategyType"], {
      is: (Level, StrategyType) =>
        +Level?.Value === 2 || +StrategyType?.Value === 2,
      then: (schema) => schema.min(1, " ")
    }),
  ObjectiveDepartments: array().of(object()),
  Code: string().required(" "),
  Order: number().required(" ").min(1, " "),
  StartDate: date().nullable()
});

function Details({ lists, formBag, isEditMode, isReadOnly, details }) {
  const intl = useIntl();
  const isAr = intl.locale === "ar";
  const { values, setFieldValue } = formBag;
  const [isStrategic, setIsStrategic] = useState(
    +values?.StrategyType?.Value === 1
  );

  const [strategyTypes, objectiveLevels] = useSelector(({ theme }) => [
    theme.StrategyTypes.item.Items,
    theme.ObjectiveLevel.item.Items
  ]);

  return (
    <WizardStep name="KPI Details">
      <Space direction="vertical" size="medium" className="details-container">
        <Row size="small">
          <Field
            name="ArabicName"
            label={intl.formatMessage({ id: "objective.arabic-title" })}
            inputProps={{
              placeholder: intl.formatMessage({ id: "objective.arabic-title" })
            }}
            disabled={isReadOnly}
            hideErrorMessage
            required
          />
          <Field
            name="EnglishName"
            label={intl.formatMessage({ id: "objective.english-title" })}
            inputProps={{
              placeholder: intl.formatMessage({
                id: "objective.english-title"
              })
            }}
            disabled={isReadOnly}
            hideErrorMessage
            required
          />
        </Row>

        <Row size="small">
          <Field
            type="textarea"
            name="ArabicDescription"
            label={intl.formatMessage({ id: "objective.arabic-description" })}
            inputProps={{
              placeholder: intl.formatMessage({
                id: "objective.arabic-description"
              })
            }}
            disabled={isReadOnly}
            hideErrorMessage
            required
          />

          <Field
            type="textarea"
            name="EnglishDescription"
            label={intl.formatMessage({ id: "objective.english-description" })}
            inputProps={{
              placeholder: intl.formatMessage({
                id: "objective.english-description"
              })
            }}
            disabled={isReadOnly}
            hideErrorMessage
            required
          />
        </Row>
        <Row size="small">
          <Field
            type="select"
            name="StrategyType"
            label={intl.formatMessage({ id: "strategy.strategy-type" })}
            inputProps={{
              options: strategyTypes,
              getOptionLabel: (el) => (isAr ? el?.Arabic : el.English),
              getOptionValue: (el) => el?.Value,
              menuPlacement: "top",
              placeholder: intl.formatMessage({ id: "strategy.strategy-type" }),
              onChange: (e) => {
                setFieldValue("StrategyType", e);
                setIsStrategic(+e.Value === 1);
                setFieldValue("Strategy", null);
                setFieldValue("Theme", null);
                setFieldValue("Pillar", null);
                setFieldValue("Level", null);
                setFieldValue("ParentID", null);
              }
            }}
            disabled={isReadOnly || isEditMode}
            hideErrorMessage
            required
          />
          <Field
            type="select"
            name="Strategy"
            label={intl.formatMessage({ id: "common.strategy" })}
            inputProps={{
              options: isStrategic
                ? lists?.strategiesWithThemes
                : lists?.strategiesWithPillars,

              getOptionLabel: (el) => (isAr ? el?.ArabicName : el.EnglishName),
              getOptionValue: (el) => el?.ID,
              menuPlacement: "top",
              isLoading: lists.loading.strategiesWithThemes,
              placeholder: intl.formatMessage({ id: "common.strategy" }),
              onChange: (e) => {
                setFieldValue("Strategy", e);
                setFieldValue("Theme", null);
                setFieldValue("Pillar", null);
                setFieldValue("Perspective", null);
                setFieldValue("ParentID", null);
              }
            }}
            disabled={isReadOnly}
            hideErrorMessage
            required
          />
        </Row>

        <Row size="small">
          {isStrategic ? (
            <Field
              type="select"
              name="Theme"
              label={intl.formatMessage({ id: "common.theme" })}
              inputProps={{
                options: values?.Strategy?.Themes || [],
                getOptionLabel: (el) =>
                  isAr ? el?.ArabicName : el.EnglishName,
                getOptionValue: (el) => el?.ID,
                menuPlacement: "top",
                placeholder: intl.formatMessage({ id: "common.theme" }),
                onChange: (e) => {
                  setFieldValue("Theme", e);
                  // setFieldValue("ParentID", null);
                }
              }}
              disabled={isReadOnly}
              hideErrorMessage
              required
            />
          ) : (
            <Field
              type="select"
              name="Pillar"
              label={intl.formatMessage({ id: "common.pillar" })}
              inputProps={{
                options: values?.Strategy?.Pillars || [],
                getOptionLabel: (el) =>
                  isAr ? el?.ArabicName : el.EnglishName,
                getOptionValue: (el) => el?.ID,
                menuPlacement: "top",
                placeholder: intl.formatMessage({ id: "common.pillar" }),
                onChange: (e) => {
                  setFieldValue("Pillar", e);
                }
              }}
              disabled={isReadOnly}
              hideErrorMessage
              required
            />
          )}

          <Field
            type="select"
            name="Perspective"
            label={intl.formatMessage({ id: "common.perspective" })}
            inputProps={{
              options:
                lists?.perspectives?.filter(
                  (e) => e?.Strategy?.ID === values?.Strategy?.ID
                ) || [],
              getOptionLabel: (el) => (isAr ? el?.ArabicName : el.EnglishName),
              getOptionValue: (el) => el?.ID,
              menuPlacement: "top",
              placeholder: intl.formatMessage({ id: "common.perspective" }),
              onChange: (e) => {
                setFieldValue("Perspective", e);
              }
            }}
            disabled={isReadOnly}
            hideErrorMessage
            required
          />
        </Row>

        {isStrategic && (
          <Row size="small">
            <Field
              type="select"
              name="Level"
              label={intl.formatMessage({ id: "common.level" })}
              inputProps={{
                options: objectiveLevels,
                getOptionLabel: (el) => (isAr ? el?.Arabic : el.English),
                getOptionValue: (el) => el?.Value,
                menuPlacement: "top",
                placeholder: intl.formatMessage({ id: "common.level" }),
                onChange: (e) => {
                  setFieldValue("Level", e);
                  setFieldValue("ParentID", null);
                }
              }}
              disabled={isReadOnly || (isEditMode && +details?.Level == 2)}
              hideErrorMessage
              required
            />
            <Field
              type="select"
              name="ParentID"
              label={intl.formatMessage({
                id: "objective.related-strategic-objective"
              })}
              inputProps={{
                options:
                  // lists?.relatedObjectives?.filter(
                  //   (e) =>
                  //     (isEditMode ? e.ID !== details.ID : true)
                  // ) || [],
                  lists?.relatedObjectives?.find(
                    (e) => e.ID === values?.Strategy?.ID
                  )?.Objectives || [],

                getOptionLabel: (el) =>
                  isAr ? el?.ArabicName : el.EnglishName,
                getOptionValue: (el) => el?.ID,
                menuPlacement: "top",
                placeholder: intl.formatMessage({
                  id: "objective.related-strategic-objective"
                }),
                onChange: (e) => {
                  setFieldValue("ParentID", e);
                }
              }}
              disabled={
                isReadOnly || +values?.Level?.Value === 1 || !values.Level
              }
              hideErrorMessage
              required
            />
          </Row>
        )}

        <Row size="small">
          <Field
            type="select"
            name="OwnerModel"
            label={intl.formatMessage({ id: "objective.owner" })}
            inputProps={{
              options: lists.users,
              getOptionLabel: (el) => el.DisplayName,
              getOptionValue: (el) => el?.UserName,
              menuPlacement: "top",
              isLoading: lists.loading.users,
              placeholder: intl.formatMessage({ id: "objective.owner" })
            }}
            disabled={isReadOnly}
            hideErrorMessage
            required
          />

          <Field
            type="select"
            name="ObjectiveSectors"
            label={intl.formatMessage({
              id: "common.sector"
            })}
            inputProps={{
              options: lists.orgStructure || [],
              getOptionLabel: (el) => (isAr ? el?.ArabicName : el.EnglishName),
              getOptionValue: (el) => el?.ID,
              menuPlacement: "top",
              isMulti: true,
              placeholder: intl.formatMessage({
                id: "common.sector"
              }),
              onChange: (e) => {
                setFieldValue("ObjectiveSectors", e);
                setFieldValue("ObjectiveExecutiveDepartments", []);
                setFieldValue("ObjectiveDepartments", []);
              }
            }}
            disabled={isReadOnly}
            hideErrorMessage
            required
          />
        </Row>

        <Row size="small">
          <Field
            type="select"
            name="ObjectiveExecutiveDepartments"
            label={intl.formatMessage({
              id: "common.executive-department"
            })}
            inputProps={{
              options:
                values?.ObjectiveSectors?.flatMap((e) => e.Childrens) || [],
              getOptionLabel: (el) => (isAr ? el?.ArabicName : el.EnglishName),
              getOptionValue: (el) => el?.ID,
              menuPlacement: "top",
              isMulti: true,
              placeholder: intl.formatMessage({
                id: "common.executive-department"
              }),
              onChange: (e) => {
                setFieldValue("ObjectiveExecutiveDepartments", e);
                setFieldValue("ObjectiveDepartments", []);
              }
            }}
            disabled={isReadOnly}
            hideErrorMessage
            required={
              +values?.Level?.Value === 2 || +values.StrategyType?.Value === 2
            }
          />

          <Field
            type="select"
            name="ObjectiveDepartments"
            label={intl.formatMessage({
              id: "common.department"
            })}
            inputProps={{
              options:
                values?.ObjectiveExecutiveDepartments?.flatMap(
                  (e) => e.Childrens
                ) || [],
              getOptionLabel: (el) => (isAr ? el?.ArabicName : el.EnglishName),
              getOptionValue: (el) => el?.ID,
              menuPlacement: "top",
              isMulti: true,
              placeholder: intl.formatMessage({
                id: "common.department"
              }),
              onChange: (e) => {
                setFieldValue("ObjectiveDepartments", e);
              }
            }}
            disabled={isReadOnly}
            hideErrorMessage
          />
        </Row>

        <Row size="small">
          <Field
            type="number"
            name="Order"
            label={intl.formatMessage({ id: "common.order" })}
            inputProps={{
              onKeyDown: (evt) => {
                if (evt.key === "e" || evt.key === "-" || evt.key === "+") {
                  evt.preventDefault();
                }
              },
              placeholder: intl.formatMessage({
                id: "common.order"
              })
            }}
            hideErrorMessage
            required
          />

          <Field
            name="Code"
            label={intl.formatMessage({ id: "common.code" })}
            inputProps={{
              placeholder: intl.formatMessage({
                id: "common.code"
              })
            }}
            hideErrorMessage
            required
          />
        </Row>

        {isStrategic ? null : (
          <Row size="small">
            <Field
              type="date"
              name="StartDate"
              disabled={isReadOnly}
              label={intl.formatMessage({ id: "common.start-date" })}
              inputProps={{
                isClearable: true,
                placeholder: intl.formatMessage({ id: "common.start-date" }),
                onChange: (e) => {
                  setFieldValue("StartDate", e);
                }
              }}
              hideErrorMessage
            />
            <div style={{ width: "100%" }} />
          </Row>
        )}
      </Space>
    </WizardStep>
  );
}

export default Details;
